import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, CircularProgress, IconButton, InputBase, Menu, MenuItem, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  Add01Icon, CheckmarkCircle02Icon, LanguageSkillIcon, MoreVerticalIcon, Refresh01Icon, Route01Icon, Search01Icon,
  ToggleOffIcon
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {useRegistryProject} from "../RegistryProjectContext";
import {SubProjectSelect} from "../SubProjectSelect";
import {AmiRepository} from "../../../repositories/AmiRepository";
import {AddExtensionModal} from "../../project/dialplan/components/AddExtensionModal";
import {getTarget} from "../../project/dialplan/components/SubProjectFields";
import {controlSx, Panel, PillSelect} from "../dashboard/components/Panel";
import {ExtensionTile} from "./ExtensionTile";
import {ExtensionEditDialog} from "./ExtensionEditDialog";
import {subProjectLabel} from "../../../utils/subProjects";

const STATUS_OPTIONS = [
  {value: "", label: "All extensions"},
  {value: "active", label: "Active"},
  {value: "disabled", label: "Disabled"},
];

const lightButtonSx = {
  height: 38, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
  color: "#23264D", border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"},
};
const darkButtonSx = {
  height: 38, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
  color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"},
};

const StatTile = ({icon, color, label, value}) => (
  <Panel sx={{p: 2, flexDirection: "row", alignItems: "center", gap: 1.5}}>
    <Box sx={{
      width: 40, height: 40, borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center",
      justifyContent: "center", color, bgcolor: `${color}1F`,
    }}>
      <HugeiconsIcon icon={icon} size={19}/>
    </Box>
    <Box>
      <Typography sx={{fontSize: 13, fontWeight: 500, color: "#6B6B80"}}>{label}</Typography>
      <Typography sx={{fontSize: 24, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em", color: "#23264D"}}>{value}</Typography>
    </Box>
  </Panel>
);

// SIP dialplan of a registry project: the extensions of every language, as cards. Each extension is owned by a
// sub project (and runs one of its versions); every AMI call goes through the project.
export const RegistryDialplanPage = () => {
  const {project, subProject} = useRegistryProject();
  const subProjects = project.subProjects;
  const projectId = project.id;

  const [connected, setConnected] = useState(null);
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [languageFilter, setLanguageFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const fetchHealth = useCallback(() => {
    setConnected(null);
    AmiRepository.getHealth(projectId)
      .then((res) => setConnected(!!res?.data?.connected))
      .catch(() => setConnected(false));
  }, [projectId]);

  // Every language's extensions in one call (the language filter narrows them on the page)
  const fetchExtensions = useCallback(() => {
    setLoading(true);
    AmiRepository.listExtensions(projectId)
      .then((res) => setExtensions((res?.data?.extensions || [])
        .sort((a, b) => String(a.extension).localeCompare(String(b.extension), undefined, {numeric: true}))))
      .catch(() => toast.error("Failed to load extensions"))
      .finally(() => setLoading(false));
  }, [projectId]);

  const refreshAll = useCallback(() => {
    fetchHealth();
    fetchExtensions();
  }, [fetchHealth, fetchExtensions]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const reloadDialplan = () => {
    setReloading(true);
    AmiRepository.reloadDialplan(projectId)
      .then((res) => res?.data?.dialplan_reloaded ? toast.success("Dialplan reloaded") : toast.error("Dialplan reload did not complete"))
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to reload dialplan"))
      .finally(() => setReloading(false));
  };

  const regenerateConf = () => {
    setMenuAnchor(null);
    AmiRepository.regenerateConf(projectId)
      .then((res) => res?.data?.conf_regenerated
        ? toast.success("Configuration file regenerated")
        : toast.error(res?.data?.conf_error || "Failed to regenerate configuration file"))
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to regenerate configuration file"));
  };

  const resync = () => {
    setMenuAnchor(null);
    AmiRepository.resyncExtensions(projectId)
      .then((res) => {
        const {synced = 0, failed = 0} = res?.data || {};
        toast.success(`Resynced ${synced} extension(s)${failed ? `, ${failed} failed` : ""}`);
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to resync extensions"));
  };

  const handleSaved = (updated) => setExtensions(prev => prev.some(e => e.extension === updated.extension)
    ? prev.map(e => (e.extension === updated.extension ? {...e, ...updated} : e))
    : [...prev, updated]);

  const handleDeleted = (deleted) => setExtensions(prev => prev.filter(e => e.id !== deleted.id));

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return extensions.filter(ext => {
      if (languageFilter && String(getTarget(ext, subProjects).subProjectId) !== languageFilter) return false;
      if (statusFilter === "active" && ext.enabled !== true) return false;
      if (statusFilter === "disabled" && ext.enabled !== false) return false;
      if (!term) return true;
      return [ext.extension, ext.name, ext.description].some(value => String(value || "").toLowerCase().includes(term));
    });
  }, [extensions, languageFilter, statusFilter, search, subProjects]);

  const activeCount = extensions.filter(ext => ext.enabled === true).length;
  const disabledCount = extensions.filter(ext => ext.enabled === false).length;
  const languagesInUse = new Set(extensions.map(ext => getTarget(ext, subProjects).subProjectId).filter(Boolean)).size;

  const connection = connected === null
    ? {label: "Checking AMI…", bg: "#F2F2F7", color: "#6B6B80"}
    : connected ? {label: "AMI connected", bg: "#E8F7EF", color: "#1E8A55"} : {label: "AMI disconnected", bg: "#FDECEC", color: "#C73A3A"};

  const languageOptions = [
    {value: "", label: "All sub projects"},
    ...subProjects.map(item => ({value: String(item.id), label: subProjectLabel(item)})),
  ];

  return (
    <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: {xs: "calc(100% - 16px)", md: "calc(100% - 128px)"}, width: "100%", mx: "auto", boxSizing: "border-box"}}>
      <AddExtensionModal
        open={addOpen}
        projectId={projectId}
        subProjects={subProjects}
        defaultSubProjectId={languageFilter ? subProjects.find(item => String(item.id) === languageFilter)?.id : subProject.id}
        handleClose={() => setAddOpen(false)}
        onCreated={(created) => {
          handleSaved(created);
          setAddOpen(false);
        }}
      />
      <ExtensionEditDialog
        extension={editing}
        projectId={projectId}
        subProjects={subProjects}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
      />

      {/* Heading */}
      <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
        <Box>
          <Stack direction={"row"} alignItems={"center"} gap={1.25}>
            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>TELEPHONY</Typography>
            <Tooltip title={"Asterisk Manager Interface connection"}>
              <Box component={"span"} sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75, px: 1, py: 0.25, borderRadius: "999px",
                fontSize: 11, fontWeight: 600, bgcolor: connection.bg, color: connection.color,
              }}>
                <Box component={"span"} sx={{width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor"}}/>
                {connection.label}
              </Box>
            </Tooltip>
          </Stack>
          <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
            SIP dialplan
          </Typography>
          <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
            Extensions callers reach, and the sub project and version each one runs
          </Typography>
        </Box>
        <Stack direction={"row"} alignItems={"center"} gap={1} flexWrap={"wrap"}>
          <SubProjectSelect size={"medium"}/>
          <Button sx={lightButtonSx} disabled={reloading} onClick={reloadDialplan}
                  startIcon={reloading ? <CircularProgress size={15} sx={{color: "#8491F7"}}/> : <HugeiconsIcon icon={Refresh01Icon} size={16}/>}>
            Reload dialplan
          </Button>
          <Button sx={darkButtonSx} onClick={() => setAddOpen(true)}
                  startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}>
            Add extension
          </Button>
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}
                      sx={{width: 38, height: 38, borderRadius: "10px !important", border: "1px solid #E4E4EE", bgcolor: "#fff"}}>
            <HugeiconsIcon icon={MoreVerticalIcon} size={17}/>
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}} transformOrigin={{vertical: "top", horizontal: "right"}}
                PaperProps={{sx: {borderRadius: "12px", mt: 0.5}}}>
            <MenuItem dense onClick={regenerateConf}>Regenerate config file</MenuItem>
            <MenuItem dense onClick={resync}>Resync extensions to Asterisk</MenuItem>
            <MenuItem dense onClick={() => {
              setMenuAnchor(null);
              refreshAll();
            }}>Refresh list</MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {/* Statistics */}
      <Box sx={{display: "grid", gap: 2, gridTemplateColumns: {xs: "1fr 1fr", lg: "repeat(4, minmax(0, 1fr))"}}}>
        <StatTile icon={Route01Icon} color={"#8491F7"} label={"Extensions"} value={loading ? "–" : extensions.length}/>
        <StatTile icon={CheckmarkCircle02Icon} color={"#2F9A6A"} label={"Active"} value={loading ? "–" : activeCount}/>
        <StatTile icon={ToggleOffIcon} color={"#C2455A"} label={"Disabled"} value={loading ? "–" : disabledCount}/>
        <StatTile icon={LanguageSkillIcon} color={"#3D86C6"} label={"Languages in use"}
                  value={loading ? "–" : `${languagesInUse}/${subProjects.length}`}/>
      </Box>

      {/* Filters */}
      <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
        <Box component={"label"} sx={{...controlSx, width: {xs: "100%", sm: 260}, boxSizing: "border-box"}}>
          <HugeiconsIcon icon={Search01Icon} size={16}/>
          <InputBase placeholder={"Search number, name or description"} value={search}
                     onChange={(e) => setSearch(e.target.value)} sx={{flex: 1, fontSize: 13, color: "#23264D"}}/>
        </Box>
        <PillSelect value={languageFilter} options={languageOptions} onChange={setLanguageFilter}
                    placeholder={"Sub project"} ariaLabel={"Filter by sub project"}/>
        <PillSelect value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter}
                    placeholder={"Status"} ariaLabel={"Filter by status"}/>
        <Typography sx={{ml: "auto", fontSize: 12, color: "#8A8AA0"}}>
          {filtered.length} of {extensions.length} extensions
        </Typography>
      </Stack>

      {/* Extensions */}
      {loading && extensions.length === 0 ? (
        <Stack alignItems={"center"} sx={{py: 6}}><CircularProgress size={26} sx={{color: "#8491F7"}}/></Stack>
      ) : filtered.length === 0 ? (
        <Panel sx={{p: 5, alignItems: "center", textAlign: "center"}}>
          <Typography sx={{fontSize: 16, fontWeight: 700, color: "#23264D"}}>
            {extensions.length ? "No extensions match these filters" : "No extensions yet"}
          </Typography>
          <Typography sx={{mt: 0.5, fontSize: 13, color: "#8A8AA0"}}>
            {extensions.length ? "Try another language, status or search." : "Add an extension so callers can reach a language's voice bot."}
          </Typography>
          {!extensions.length && (
            <Button sx={{...darkButtonSx, mt: 2}} onClick={() => setAddOpen(true)} startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}>
              Add extension
            </Button>
          )}
        </Panel>
      ) : (
        <Box sx={{display: "grid", gap: 2, gridTemplateColumns: {xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))"}}}>
          {filtered.map(ext => (
            <ExtensionTile
              key={ext.id || ext.extension}
              extension={ext}
              projectId={projectId}
              subProjects={subProjects}
              onEdit={setEditing}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
};
